<template>
  <BaseCard :class="$style.base">
    <SendMessage
      v-if="showMessageModal"
      :customerData="customerData"
      selectedMessageType="email"
      @close="showMessageModal = false"
    />
    <div :class="$style.list">
      <div
        v-if="hasFeatureFlag('module-appointments')"
        :class="$style.listItem"
        @click="onCreateAppointment"
        v-test="'create-appointment'"
      >
        <BaseText
          iconBefore="calendar"
          bold
          >{{ $t('global.actions.create_appointment') }}</BaseText
        >
      </div>
      <div
        v-if="hasFeatureFlag('module-register')"
        :class="$style.listItem"
        @click="onCreateInvoice"
        v-test="'create-sale'"
      >
        <BaseText
          iconBefore="shopping-cart"
          bold
        >
          {{ $t(`global.actions.create_${orderKey()}`) }}
        </BaseText>
      </div>
      <div
        v-if="hasFeatureFlag('customer-forms') && hasFillableForms"
        :class="$style.listItem"
        @click="onFillInForm"
        v-test="'customerFillInForm'"
      >
        <BaseText
          iconBefore="file"
          bold
          >{{ $t('global.actions.fill_in_form') }}</BaseText
        >
      </div>
      <div
        v-if="hasFeatureFlag('appointment-reports')"
        :class="$style.listItem"
        @click="onCreateReport"
        v-test="'customerCreateReport'"
      >
        <BaseText
          iconBefore="check-square"
          bold
          >{{ $t('global.actions.create_report') }}</BaseText
        >
      </div>
      <div
        :class="$style.listItem"
        @click="showMessageModal = true"
        v-test="'customerSendMessage'"
      >
        <BaseText
          iconBefore="email"
          bold
          >{{ $t('customers.send_message') }}</BaseText
        >
      </div>
      <div
        v-if="canSendAppointmentOverview"
        :class="$style.listItem"
        @click="showAppointmentRequestConfirmation"
        v-test="'customerSendAppointmentOverview'"
      >
        <BaseText
          iconBefore="email"
          bold
        >
          {{ $t('customers.send_upcoming_appointments') }}
        </BaseText>
      </div>
    </div>
  </BaseCard>
</template>

<script lang="ts">
import { useQuery } from '@vue/apollo-composable';
import gql from 'graphql-tag';
import { mapState } from 'pinia';
import { computed, defineComponent, inject } from 'vue';
import { orderKey } from '@/helpers/compliant-register';
import SendMessage from '@/modules/customers/SendMessage.vue';
import { usePreCreateStore } from '@/stores/calendar-pre-create';
import { useUserStore } from '@/stores/user';
import unleash from '@/unleash';
import { useAppointmentOverview } from '../appointmentOverview';

export default defineComponent({
  components: {
    SendMessage,
  },
  props: {
    summary: {
      type: Object,
      required: false,
    },
  },
  setup(props) {
    const customer = inject<any>('customer');

    const { result } = useQuery(
      gql`
        query getCurrentUser {
          currentUser {
            company {
              hasFillableForms
            }
          }
        }
      `,
      null,
      {
        fetchPolicy: 'cache-first',
      },
    );

    const appointmentsCount = computed(
      () => props.summary?.appointmentCount || 0,
    );

    const { showModal: showAppointmentRequestConfirmation } =
      useAppointmentOverview({
        appointmentsCount,
      });

    const hasFillableForms = computed(
      () => !!result.value?.currentUser.company.hasFillableForms,
    );

    return {
      hasFillableForms,
      customer,
      orderKey,
      showAppointmentRequestConfirmation,
    };
  },
  data() {
    return {
      showMessageModal: false,
    };
  },
  computed: {
    ...mapState(useUserStore, ['hasFeatureFlag']),
    customerData() {
      return {
        customerId: this.customer.id,
        customerName: this.customer.firstName,
        customerMobile: this.customer.mobilePhone,
        customerEmail: this.customer.email,
      };
    },
    canSendAppointmentOverview() {
      return !!this.summary && unleash.isEnabled('AppointmentOverviewRequest');
    },
  },
  methods: {
    onCreateAppointment() {
      this.$router.push({ name: 'calendar' });

      const { setState } = usePreCreateStore();
      setState({
        action: 'CREATE',
        customer: {
          firstName: this.customer.firstName,
          lastName: this.customer.lastName,
          email: this.customer.email,
          mobilePhone: this.customer.mobilePhone,
          id: this.customer.id,
        },
      });
    },
    onCreateInvoice() {
      const query = { customerId: this.customer.id };
      this.$router.push({ name: 'register', query });
    },
    onFillInForm() {
      this.$router.push({ name: 'customer-forms-select' });
    },
    onCreateReport() {
      this.$router.push({ name: 'customer-report-create' });
    },
  },
});
</script>

<style
  lang="scss"
  module
>
.list {
  display: flex;
  flex-direction: column;
  margin: -$spacing;
  // Clip the hover fill to the card's rounded corners (minus its 1px border)
  border-radius: $radius * 2 - 1px;
  overflow: hidden;
}

.listItem {
  display: flex;
  align-items: center;
  padding: $spacing;
  text-decoration: none;
  cursor: pointer;

  &:not(:last-of-type) {
    border-bottom: 1px solid var(--color-border);
  }

  &:focus {
    background: var(--color-highlight);
  }

  @include hover {
    background: var(--color-highlight);
  }
}
</style>
